import type { GetServerSidePropsContext } from "next";
import type { BasePageProps } from "@/types";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";

import MenuScreen from "@/components/menu/MenuScreen";
import { ORDER_TYPE_ENUM, QUERY_PARAM_ENUM } from "@/constants/enums";
import useSelectDineinTable from "@/hooks/useSelectDineinTable";
import { loadBasePageProps } from "@/lib/pageProps";
import { useAppDispatch, useAppSelector } from "@/store";
import { setOrderType } from "@/store/slices/cartSlice";
import { findTableInLayout, loadDineinLayout } from "@/store/slices/dineinSlice";

/**
 * `/menu/dine-in` — the QR-code entry point, and the same menu as `/menu`.
 *
 * **This route is not a table picker.** The original renders `MenuBody` and
 * `CartPanel` here, exactly as `/menu` does, and takes the table from the QUERY
 * STRING — `?floor_id=…&table_id=…`, the link printed on the QR sticker each
 * table carries. There is no dine-in card on the landing page and no way to
 * reach this route without those two ids: a customer scans, lands on the menu
 * with their table already on the basket, and orders. Staff, who have no QR code
 * to scan, pick a table from `DINE_IN_SEARCH_TABLES_MODAL` on the checkout —
 * which is why that picker's rows are `disabled` for everyone else.
 *
 * The page that used to live here was invented: a "Which table are you at?"
 * empty state with a free-text table-number box that wrote
 * `{ tableId: <typed text>, floorId: null }` onto the cart. That could never
 * satisfy the dine-in gate (which needs BOTH ids) and the typed text matched no
 * table on any floor, so it could not have produced an order the POS could seat.
 * It is gone.
 *
 * Three effects, all the original's:
 *
 * 1. `web_enable_dine_in` gates the route — the original's data handler does
 *    `H ? (…load…) : z.push("/")`, so a tenant without dine-in is redirected
 *    home rather than shown a menu it cannot take an order from;
 * 2. the dine-in data load asks for `dineinLayout`, which nothing else fetches;
 * 3. `A()` reads `floor_id` / `table_id` off `window.location.search` and hands
 *    them to `setUpdatePreselectTableCartInfoOnInit` (`CV`).
 *
 * `CV`'s own second half — `fetchDineinOrderDetails`, which pulls the table's
 * OPEN order into the cart — is not reproduced, for the reason recorded on
 * `TableSearchModal`: this tree's cart is a basket with no `_id` and no
 * load-an-order path. The preselect half, which is what makes the QR link work,
 * is complete.
 */
export default function DineInPage({ data, metadata, baseUrl }: BasePageProps) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  useEffect(() => {
    if (isStaff && router.isReady && !router.query[QUERY_PARAM_ENUM.tableId]) void router.replace("/staff/tables");
  }, [isStaff, router]);
  const tenantId = useAppSelector((state) => state.settings.data._id);
  const dineInEnabled = useAppSelector(
    (state) => state.settings.data.web_enable_dine_in,
  );
  const mappedData = useAppSelector((state) => state.dinein.mappedData);
  const selectTable = useSelectDineinTable();
  // The preselect runs once per set of ids, not once per layout change — a
  // second run would overwrite a guest count the customer has since changed.
  const preselected = useRef<string | null>(null);
  // `refetchKey` — the original's periodic reload. See the effect below.
  const [refetchKey, setRefetchKey] = useState(0);
  const lastRefetchAt = useRef(Date.now());

  /**
   * THE FLOOR-PLAN POLL — the original's own mechanism, and the only one it has.
   *
   * The storefront has NO socket: `socket.io`, `WebSocket`, `EventSource` and
   * `onSnapshot` appear nowhere in its bundle, and the `socketInitiate*`
   * reducers in its store are shared POS code with nothing feeding them on the
   * web build. It keeps the floor plan current with a 60-second ticker that
   * bumps a `refetchKey` once 40 minutes have elapsed (hook module 300,
   * `live-chunks/677-…js`):
   *
   * ```js
   * j.current = setInterval(() => {
   *   …
   *   let n = Date.now();
   *   (n - Z.current) / 6e4 >= 40 && (I(e => e + 1), (Z.current = n));
   * }, 6e4)
   * ```
   *
   * and `/menu/dine-in` refetches on that key, with `dineinLayout` in the list
   * unconditionally — it is the one key that is re-fetched even when everything
   * else is already cached (`e.length > 0 ? B(e) : B(["dineinLayout"])`).
   *
   * The two constants are the original's. Shortening `REFETCH_AFTER_MS` is the
   * only lever there is for a fresher floor plan, since there is no push channel
   * to adopt; note that it cannot fix a table the SERVER still believes is
   * seated, which is a separate write-path defect.
   */
  useEffect(() => {
    if (!tenantId || !dineInEnabled) return;
    const TICK_MS = 60_000;
    const REFETCH_AFTER_MS = 40 * 60_000;
    lastRefetchAt.current = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      if (now - lastRefetchAt.current < REFETCH_AFTER_MS) return;
      lastRefetchAt.current = now;
      setRefetchKey((key) => key + 1);
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [tenantId, dineInEnabled]);

  // 1. `H ? … : z.push("/")`. The original decides this INSIDE its data
  // handler, i.e. only once settings have arrived, so the guard here is the
  // tenant id — the same value that proves the payload landed.
  useEffect(() => {
    if (!tenantId) return;
    if (!dineInEnabled) void router.push("/");
  }, [dineInEnabled, router, tenantId]);

  // The basket is dine-in from the moment this route is opened.
  useEffect(() => {
    dispatch(setOrderType(ORDER_TYPE_ENUM.DINE_IN));
  }, [dispatch]);

  // 2. `dineinLayout` — the floor plan, fetched here and nowhere else, on mount
  // AND on every tick of `refetchKey` below.
  useEffect(() => {
    if (!tenantId || !dineInEnabled) return;
    void dispatch(loadDineinLayout(tenantId));
  }, [dispatch, tenantId, dineInEnabled, refetchKey]);

  // 3. `A()` — the QR deep link. The ids are read from the URL, then resolved
  // against the layout, because the cart needs the table's NAME and its current
  // sitting and the sticker carries only ids.
  useEffect(() => {
    const raw = router.query[QUERY_PARAM_ENUM.tableId];
    const rawFloor = router.query[QUERY_PARAM_ENUM.floorId];
    const tableId = Array.isArray(raw) ? raw[0] : raw;
    const floorId = Array.isArray(rawFloor) ? rawFloor[0] : rawFloor;
    if (!tableId || !floorId) return;
    const key = `${floorId}/${tableId}`;
    if (preselected.current === key) return;
    const table = findTableInLayout({ mappedData, floorId, tableId });
    if (!table) return;
    preselected.current = key;
    selectTable(table);
  }, [mappedData, router.query, selectTable]);

  return (
    <MenuScreen data={data} metadata={metadata} baseUrl={baseUrl} />
  );
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<{ props: BasePageProps }> {
  // The BRANCH is resolved first and both store fetches are made for it, so the
  // server render is already the chosen outlet's. See lib/pageProps.ts.
  return { props: await loadBasePageProps(ctx) };
}
